import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { documentsService } from '../services/documents.service'
import { Document, DocumentType, DocumentStatus, UserDocument } from '../types'
import toast from 'react-hot-toast'

export function useCaseDocuments(caseId: string) {
  return useQuery({
    queryKey: ['documents', 'case', caseId],
    queryFn: () => documentsService.getCaseDocuments(caseId),
    enabled: !!caseId,
    staleTime: 2 * 60 * 1000, // 2 minutes
  })
}

export function useDocumentChecklist(caseId: string) {
  return useQuery({
    queryKey: ['documents', 'checklist', caseId],
    queryFn: () => documentsService.getDocumentChecklist(caseId),
    enabled: !!caseId,
    staleTime: 2 * 60 * 1000,
  })
}

export function useCaseDocumentsSummary(caseId: string) {
  return useQuery({
    queryKey: ['documents', 'summary', caseId],
    queryFn: () => documentsService.getCaseDocumentsSummary(caseId),
    enabled: !!caseId,
    staleTime: 2 * 60 * 1000,
  })
}

export function useDocumentVerificationStatus(caseId: string) {
  return useQuery({
    queryKey: ['documents', 'verification', caseId],
    queryFn: () => documentsService.verifyAllRequiredDocuments(caseId),
    enabled: !!caseId,
    staleTime: 2 * 60 * 1000,
  })
}

export function useDocument(id: string) {
  return useQuery({
    queryKey: ['document', id],
    queryFn: () => documentsService.getDocumentById(id),
    enabled: !!id,
    staleTime: 5 * 60 * 1000, // 5 minutes
  })
}

export function useUploadDocument() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ caseId, file, documentType }: {
      caseId: string
      file: File
      documentType: DocumentType
    }) => {
      const formData = new FormData()
      formData.append('file', file)
      formData.append('caseId', caseId)
      formData.append('documentType', documentType)
      return documentsService.uploadDocument(formData)
    },
    onSuccess: (newDocument, variables) => {
      toast.success('Document uploaded successfully')
      queryClient.invalidateQueries({ queryKey: ['documents', 'case', variables.caseId] })
      queryClient.invalidateQueries({ queryKey: ['documents', 'checklist', variables.caseId] })
      queryClient.invalidateQueries({ queryKey: ['documents', 'summary', variables.caseId] })
      queryClient.invalidateQueries({ queryKey: ['documents', 'verification', variables.caseId] })
    },
    onError: (error: any) => {
      const message = error.response?.data?.message || 'Failed to upload document'
      toast.error(message)
    },
  })
}

export function useUpdateDocumentStatus() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, status, verificationNotes }: {
      id: string
      status: DocumentStatus
      verificationNotes?: string
    }) => documentsService.updateDocumentStatus(id, status, verificationNotes),
    onSuccess: (updatedDocument, variables) => {
      const successMessage = variables.status === DocumentStatus.VERIFIED
        ? 'Document verified successfully'
        : `Document ${variables.status.toLowerCase()} successfully`

      toast.success(successMessage)

      // Invalidate relevant queries
      queryClient.invalidateQueries({ queryKey: ['document', variables.id] })

      // If we know the case ID, invalidate case-related queries
      // Note: We might need to fetch the document to get the caseId
    },
    onError: (error: any) => {
      const message = error.response?.data?.message || 'Failed to update document status'
      toast.error(message)
    },
  })
}

export function useVerifyDocument() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, verificationNotes }: {
      id: string
      verificationNotes?: string
    }) => documentsService.verifyDocument(id, verificationNotes),
    onSuccess: (updatedDocument) => {
      toast.success('Document verified successfully')
      queryClient.invalidateQueries({ queryKey: ['document', updatedDocument.id] })
      queryClient.invalidateQueries({ queryKey: ['documents'] })
    },
    onError: (error: any) => {
      const message = error.response?.data?.message || 'Failed to verify document'
      toast.error(message)
    },
  })
}

export function useRejectDocument() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, verificationNotes }: {
      id: string
      verificationNotes?: string
    }) => documentsService.rejectDocument(id, verificationNotes),
    onSuccess: (updatedDocument) => {
      toast.success('Document rejected')
      queryClient.invalidateQueries({ queryKey: ['document', updatedDocument.id] })
      queryClient.invalidateQueries({ queryKey: ['documents'] })
    },
    onError: (error: any) => {
      const message = error.response?.data?.message || 'Failed to reject document'
      toast.error(message)
    },
  })
}

export function useDeleteDocument() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string) => documentsService.deleteDocument(id),
    onSuccess: (_, documentId) => {
      toast.success('Document deleted successfully')
      queryClient.invalidateQueries({ queryKey: ['document', documentId] })
      queryClient.invalidateQueries({ queryKey: ['documents'] })
    },
    onError: (error: any) => {
      const message = error.response?.data?.message || 'Failed to delete document'
      toast.error(message)
    },
  })
}

// Admin hooks for document management
export function useAdminPendingDocuments() {
  return useQuery({
    queryKey: ['documents', 'admin', 'pending'],
    queryFn: () => documentsService.getAdminPendingDocuments(),
    staleTime: 1 * 60 * 1000, // 1 minute
  })
}

export function useAdminAllDocuments() {
  return useQuery({
    queryKey: ['documents', 'admin', 'all'],
    queryFn: () => documentsService.getAdminAllDocuments(),
    staleTime: 2 * 60 * 1000, // 2 minutes
  })
}

export function useDownloadDocument() {
  return useMutation({
    mutationFn: ({ id, filename }: { id: string; filename: string }) =>
      documentsService.downloadDocument(id).then((blob) => {
        const url = window.URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.download = filename
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        window.URL.revokeObjectURL(url)
      }),
    onError: (error: any) => {
      const message = error.response?.data?.message || 'Failed to download document'
      toast.error(message)
    },
  })
}

// Provider document hooks
export function useProviderDocuments() {
  return useQuery({
    queryKey: ['documents', 'provider'],
    queryFn: () => documentsService.getProviderDocuments(),
    staleTime: 2 * 60 * 1000, // 2 minutes
  })
}

export function useUploadProviderDocument() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ file, documentType }: {
      file: File
      documentType: DocumentType
    }) => {
      const formData = new FormData()
      formData.append('document', file)
      formData.append('documentType', documentType)
      return documentsService.uploadProviderDocument(formData)
    },
    onSuccess: () => {
      toast.success('Document uploaded successfully')
      queryClient.invalidateQueries({ queryKey: ['documents', 'provider'] })
    },
    onError: (error: any) => {
      const message = error.response?.data?.message || 'Failed to upload document'
      toast.error(message)
    },
  })
}

export function useDeleteProviderDocument() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string) => documentsService.deleteProviderDocument(id),
    onSuccess: () => {
      toast.success('Document deleted successfully')
      queryClient.invalidateQueries({ queryKey: ['documents', 'provider'] })
    },
    onError: (error: any) => {
      const message = error.response?.data?.message || 'Failed to delete document'
      toast.error(message)
    },
  })
}

export function useVerifyProviderDocument() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, verificationNotes }: {
      id: string
      verificationNotes?: string
    }) => documentsService.verifyProviderDocument(id, verificationNotes),
    onSuccess: (updatedDocument) => {
      toast.success('Provider document verified successfully')
      queryClient.invalidateQueries({ queryKey: ['documents', 'admin', 'all'] })
      queryClient.invalidateQueries({ queryKey: ['documents', 'admin', 'pending'] })
    },
    onError: (error: any) => {
      const message = error.response?.data?.message || 'Failed to verify provider document'
      toast.error(message)
    },
  })
}

export function useRejectProviderDocument() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, verificationNotes }: {
      id: string
      verificationNotes?: string
    }) => documentsService.rejectProviderDocument(id, verificationNotes),
    onSuccess: (updatedDocument) => {
      toast.success('Provider document rejected')
      queryClient.invalidateQueries({ queryKey: ['documents', 'admin', 'all'] })
      queryClient.invalidateQueries({ queryKey: ['documents', 'admin', 'pending'] })
    },
    onError: (error: any) => {
      const message = error.response?.data?.message || 'Failed to reject provider document'
      toast.error(message)
    },
  })
}

// User (family) document hooks
export function useUserDocuments() {
  return useQuery({
    queryKey: ['documents', 'user'],
    queryFn: () => documentsService.getUserDocuments(),
    staleTime: 2 * 60 * 1000, // 2 minutes
  })
}

export function useUploadUserDocument() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ file, documentType }: {
      file: File
      documentType: DocumentType
    }) => {
      const formData = new FormData()
      formData.append('document', file)
      formData.append('documentType', documentType)
      return documentsService.uploadUserDocument(formData)
    },
    onSuccess: () => {
      toast.success('Document uploaded successfully')
      queryClient.invalidateQueries({ queryKey: ['documents', 'user'] })
      queryClient.invalidateQueries({ queryKey: ['profile'] })
      queryClient.invalidateQueries({ queryKey: ['profileCompletion'] })
    },
    onError: (error: any) => {
      const message = error.response?.data?.message || 'Failed to upload document'
      toast.error(message)
    },
  })
}

export function useDeleteUserDocument() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string) => documentsService.deleteUserDocument(id),
    onSuccess: () => {
      toast.success('Document deleted successfully')
      queryClient.invalidateQueries({ queryKey: ['documents', 'user'] })
      queryClient.invalidateQueries({ queryKey: ['profile'] })
      queryClient.invalidateQueries({ queryKey: ['profileCompletion'] })
    },
    onError: (error: any) => {
      const message = error.response?.data?.message || 'Failed to delete document'
      toast.error(message)
    },
  })
}

export function useDownloadUserDocument() {
  return useMutation({
    mutationFn: ({ id, filename }: { id: string; filename: string }) =>
      documentsService.downloadUserDocument(id).then((blob) => {
        const url = window.URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.download = filename
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        window.URL.revokeObjectURL(url)
      }),
    onError: (error: any) => {
      const message = error.response?.data?.message || 'Failed to download document'
      toast.error(message)
    },
  })
}

export function useVerifyUserDocument() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, verificationNotes }: {
      id: string
      verificationNotes?: string
    }) => documentsService.verifyUserDocument(id, verificationNotes),
    onSuccess: () => {
      toast.success('User document verified successfully')
      queryClient.invalidateQueries({ queryKey: ['documents', 'admin', 'user-all'] })
      queryClient.invalidateQueries({ queryKey: ['documents', 'admin', 'user-pending'] })
    },
    onError: (error: any) => {
      const message = error.response?.data?.message || 'Failed to verify user document'
      toast.error(message)
    },
  })
}

export function useRejectUserDocument() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, verificationNotes }: {
      id: string
      verificationNotes?: string
    }) => documentsService.rejectUserDocument(id, verificationNotes),
    onSuccess: () => {
      toast.success('User document rejected')
      queryClient.invalidateQueries({ queryKey: ['documents', 'admin', 'user-all'] })
      queryClient.invalidateQueries({ queryKey: ['documents', 'admin', 'user-pending'] })
    },
    onError: (error: any) => {
      const message = error.response?.data?.message || 'Failed to reject user document'
      toast.error(message)
    },
  })
}

export function usePendingUserDocuments() {
  return useQuery({
    queryKey: ['documents', 'admin', 'user-pending'],
    queryFn: () => documentsService.getAdminPendingUserDocuments(),
    staleTime: 1 * 60 * 1000, // 1 minute
  })
}

export function useAllUserDocuments() {
  return useQuery({
    queryKey: ['documents', 'admin', 'user-all'],
    queryFn: () => documentsService.getAdminAllUserDocuments(),
    staleTime: 2 * 60 * 1000, // 2 minutes
  })
}